import { useMemo, useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import { useAggregatedStats, useUserSites, useDashboardData, useCreateDefaultSite } from '../hooks/useApi';
import { OnboardingFlow } from '../components/OnboardingFlow';
import { UpgradePrompt } from '../components/UpgradePrompt';
import { DataExport } from '../components/DataExport';
import { PlanExpiryAlert } from '../components/PlanExpiryAlert';
import { 
  GeoLocationChart, 
  DateDistributionChart, 
  PagesDistributionChart, 
  VoteCountChart, 
  TopCommentsDisplay 
} from '../components/charts';

// Loading skeleton component
function StatCardSkeleton() {
  return (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="p-5">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-gray-200 rounded animate-pulse"></div>
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate">
                <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
              </dt>
              <dd className="mt-1">
                <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
              </dd>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}

// Error state component
function ErrorState({ error }: { error: Error }) {
  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-6">
      <div className="flex">
        <div className="flex-shrink-0">
          <div className="text-red-400 text-2xl">⚠️</div>
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-red-800">
            Unable to load dashboard data
          </h3>
          <div className="mt-2 text-sm text-red-700">
            <p>{error.message}</p>
          </div>
          <div className="mt-4">
            <div className="-mx-2 -my-1.5 flex">
              <button
                type="button"
                className="bg-red-50 px-2 py-1.5 rounded-md text-sm font-medium text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-red-50 focus:ring-red-600"
                onClick={() => window.location.reload()}
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function DashboardPage() {
  const { data: sites, isLoading: sitesLoading, error: sitesError } = useUserSites();
  const { data: aggregatedStats, isLoading: aggregatedLoading, error: aggregatedError } = useAggregatedStats();
  const createDefaultSite = useCreateDefaultSite();
  const [hasAttemptedDefaultSiteCreation, setHasAttemptedDefaultSiteCreation] = useState(false);
  
  // Check if user has any paid sites


  const hasPaidSites = sites?.some(site => site.plan === 'pro' || site.plan === 'enterprise') || false;
  const tempOverride = true;
  
  // Memoize the first site ID to prevent unnecessary re-renders and API calls
  const firstSiteId = useMemo(() => {
    return sites?.[0]?.id || null;
  }, [sites]);
  
  // Only fetch detailed stats if we have a valid site ID
  const { stats, isLoading: statsLoading, error: statsError } = useDashboardData(
    firstSiteId || undefined
  );

  // Automatically create default site for new users with no sites
  useEffect(() => {
    const shouldCreateDefaultSite = 
      !sitesLoading && 
      !sitesError && 
      sites && 
      sites.length === 0 && 
      !hasAttemptedDefaultSiteCreation &&
      !createDefaultSite.isPending;

    if (shouldCreateDefaultSite) {
      setHasAttemptedDefaultSiteCreation(true);
      
      createDefaultSite.mutateAsync()
        .then((result) => {
          toast.success(`Default site created! Site ID: ${result.siteId}`);
        })
        .catch((error) => {
          console.error('Failed to create default site:', error);
          toast.error('Failed to create default site. Please try refreshing the page.');
        });
    }
  }, [sites, sitesLoading, sitesError, hasAttemptedDefaultSiteCreation, createDefaultSite]);

  // Show onboarding flow for new users with no sites
  if (!sitesLoading && !sitesError && sites && sites.length === 0) {
    return <OnboardingFlow />;
  }

  // Show error state if there's an error loading sites or stats
  if (sitesError || aggregatedError || statsError) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-600">
            Overview of your feedback analytics
          </p>
        </div>
        <ErrorState error={(sitesError || aggregatedError || statsError) as Error} />
      </div>
    );
  }

  const isLoading = sitesLoading || aggregatedLoading || (firstSiteId ? statsLoading : false);
  const detailedStats = stats.data;

  return (
    <div className="space-y-6">
      {/* Plan Expiry Alert */}
      {sites && sites.length > 0 && (
        <PlanExpiryAlert sites={sites} />
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="mt-1 text-sm text-gray-600">
            Comprehensive feedback insights and performance metrics
          </p>
        </div>
        {sites && sites.length > 0 && (
          <div className="text-right">
            <div className="text-sm text-gray-500">
              {sites.length} {sites.length === 1 ? 'site' : 'sites'} active
            </div>
            {firstSiteId && (
              <div className="text-xs text-gray-400">
                Showing detailed data for: {sites[0]?.domain}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {isLoading ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : (
          <>
            <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="text-2xl">📊</div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Total Votes
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {aggregatedStats?.totalVotes?.toLocaleString() || '0'}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="text-2xl">👍</div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Positive Feedback
                      </dt>
                      <dd className="text-lg font-medium text-green-600">
                        {aggregatedStats?.upVotes?.toLocaleString() || '0'}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="text-2xl">📈</div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Conversion Rate
                      </dt>
                      <dd className="text-lg font-medium text-blue-600">
                        {aggregatedStats?.conversionRate 
                          ? `${(aggregatedStats.conversionRate * 100).toFixed(1)}%` 
                          : '0%'
                        }
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="text-2xl">🌐</div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Active Sites
                      </dt>
                      <dd className="text-lg font-medium text-purple-600">
                        {aggregatedStats?.sites || '0'}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Main Analytics Charts */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Vote Count Visualization */}
        {hasPaidSites || tempOverride ? (
          <VoteCountChart
            totalVotes={detailedStats?.totalVotes || aggregatedStats?.totalVotes || 0}
            upVotes={detailedStats?.upVotes || aggregatedStats?.upVotes || 0}
            downVotes={detailedStats?.downVotes || aggregatedStats?.downVotes || 0}
            conversionRate={detailedStats?.conversionRate || aggregatedStats?.conversionRate || 0}
            isLoading={isLoading}
          />
        ) : (
          <UpgradePrompt
            title="Vote Analytics"
            description="Detailed vote breakdown and conversion metrics are available with Pro plans."
          />
        )}

        {/* Date Distribution Chart */}
        {hasPaidSites || tempOverride ? (
          <DateDistributionChart
            timeSeries={detailedStats?.timeSeries || []}
            isLoading={isLoading}
          />
        ) : (
          <UpgradePrompt
            title="Vote Trends Over Time"
            description="Historical vote trends and time-series analytics are available with Pro plans."
          />
        )}
      </div>

      {/* Geographic and Page Analytics */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Geographic Distribution */}
        {hasPaidSites || tempOverride ? (
          <GeoLocationChart
            countries={detailedStats?.countries || {}}
            regions={detailedStats?.regions || {}}
            cities={detailedStats?.cities || {}}
            isLoading={isLoading}
          />
        ) : (
          <UpgradePrompt
            title="Geographic Distribution"
            description="Location-based analytics showing where your feedback comes from are available with Pro plans."
          />
        )}

        {/* Pages Distribution */}
        <PagesDistributionChart
          pages={detailedStats?.pages || {}}
          isLoading={isLoading}
        />
      </div>

      {/* Comments Section */}
      <TopCommentsDisplay
        comments={detailedStats?.comments || []}
        isLoading={isLoading}
      />

      {/* Data Export Section */}
      <DataExport
        siteId={firstSiteId || ''}
        hasPaidSites={hasPaidSites}
      />

      {/* Footer Note */}
      {!isLoading && sites && sites.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <div className="text-blue-400 text-lg">💡</div>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Dashboard Insights
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  Your dashboard shows real-time feedback analytics. Data is aggregated every 5 minutes 
                  and includes geographic insights, page performance, and customer sentiment analysis.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 